(() => {
  if (customElements.get('jalp-mini-pack')) return;

  class JalpMiniPack extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.contentsMedia.addEventListener('change', this.moveContents);
        this.moveContents();
        return;
      }
      const source = this.querySelector('[data-pack-data]');
      if (!source) return;
      try { this.data = JSON.parse(source.textContent); } catch { return; }
      this.form = this.querySelector('[data-jalp-form]');
      this.select = this.querySelector('[data-pack-variant]');
      this.quantityInput = this.querySelector('[data-pack-quantity]');
      this.errorElement = this.querySelector('[data-pack-error]');
      this.flavorFieldset = this.querySelector('[data-pack-flavors]');
      if (!this.form || !this.select || !this.quantityInput) return;
      this.initialized = true;
      this.normalize = value => String(value).normalize('NFKD').toLowerCase().replace(/[^a-z0-9]/g, '');
      this.data.variants.forEach(variant => { variant.size = Number.parseInt(variant.size, 10) || 6; });
      this.busy = false;
      this.box = 1;
      this.packSize = 6;
      const initial = this.data.variants.find(variant => variant.id === this.data.initialVariant);
      if (initial) this.packSize = initial.size;
      else if (!this.data.variants.some(variant => variant.size === 6 && variant.available)) {
        this.packSize = this.data.variants.find(variant => variant.available)?.size || 6;
      }
      const initialChoice = initial ? this.choiceFromVariant(initial) : null;
      this.boxes = Array.from({ length: this.packSize / 6 }, () => initialChoice ? { ...initialChoice } : null);
      // Mixed options are absent from the no-JS form: their box details require this builder.
      this.data.variants.filter(variant => this.isMixed(variant)).forEach(variant => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = variant.title;
        option.disabled = !variant.available;
        this.select.append(option);
      });
      this.querySelectorAll('[data-pack-enhanced]').forEach(element => { element.hidden = false; });
      this.querySelector('[data-pack-fallback]').hidden = true;
      this.select.required = false;
      this.querySelectorAll('[data-pack-size]').forEach(button => button.addEventListener('click', () => this.chooseSize(Number(button.dataset.packSize))));
      this.querySelectorAll('[data-pack-mode]').forEach(button => button.addEventListener('click', () => this.chooseMode(button.dataset.packMode)));
      this.querySelectorAll('[data-pack-flavor]').forEach(button => button.addEventListener('click', () => this.chooseFlavor(button.dataset.packFlavor)));
      this.querySelector('[data-pack-progress]').addEventListener('click', () => {
        if (this.busy) return;
        const next = this.choiceKey(this.currentChoice) ? this.nextUnchosenBox() : this.box - 1;
        if (next !== null) this.chooseBox(next + 1);
        this.flavorFieldset.focus({ preventScroll: true });
        this.flavorFieldset.scrollIntoView({ block: 'center', behavior: 'auto' });
      });
      this.querySelectorAll('[data-pack-change]').forEach(button => button.addEventListener('click', () => {
        this.quantityInput.value = String(Math.max(1, (this.quantity() || 1) + Number(button.dataset.packChange)));
        this.clearError(); this.render();
      }));
      this.querySelector('[data-pack-next-box]').addEventListener('click', () => {
        const next = this.nextUnchosenBox();
        if (next !== null) this.chooseBox(next + 1);
      });
      this.quantityInput.addEventListener('input', () => { this.clearError(); this.render(); });
      this.form.addEventListener('submit', event => this.addToCart(event));
      this.querySelector('[data-pack-sticky-add]').addEventListener('click', () => this.form.requestSubmit());
      this.contentsList = this.querySelector('[data-pack-contents-list]');
      this.contentsMedia = window.matchMedia('(max-width: 989px)');
      this.moveContents = () => {
        const target = this.querySelector(this.contentsMedia.matches ? '[data-pack-contents-mobile]' : '[data-pack-contents-home]');
        if (this.contentsList.parentElement !== target) target.prepend(this.contentsList);
      };
      this.contentsMedia.addEventListener('change', this.moveContents);
      this.moveContents();
      this.render();
    }

    disconnectedCallback() { this.contentsMedia?.removeEventListener('change', this.moveContents); }

    get currentChoice() { return this.boxes[this.box - 1]; }
    get mode() { return this.currentChoice?.mode || null; }
    get flavor() { return this.data.flavors.find(flavor => flavor.key === this.currentChoice?.flavorKey) || null; }
    quantity() { const value = Number(this.quantityInput.value); return Number.isSafeInteger(value) && value >= 1 ? value : 0; }
    isOpen() { return this.data.ordersOpen && !(this.data.cutoff > 0 && Date.now() >= this.data.cutoff * 1000); }
    closedLabel() { return this.data.cutoffPassed || (this.data.cutoff > 0 && Date.now() >= this.data.cutoff * 1000) ? this.data.strings.preordersClosed : this.data.strings.preordersSoon; }
    isVariety(variant) { return ['varietypack', 'varietypackoneofeach'].includes(this.normalize(variant?.pack)); }
    isMixed(variant) { return this.normalize(variant?.pack) === 'mixedboxes'; }
    choiceFromVariant(variant) {
      if (this.isVariety(variant)) return { mode: 'variety' };
      const flavor = this.data.flavors.find(item => this.normalize(item.variantTitle) === this.normalize(variant.pack));
      return flavor ? { mode: 'single', flavorKey: flavor.key } : null;
    }
    choiceKey(choice) { return choice?.mode === 'variety' ? 'variety' : this.data.flavors.some(flavor => flavor.key === choice?.flavorKey) ? choice.flavorKey : ''; }
    complete() { return this.boxes.length === this.packSize / 6 && this.boxes.every(choice => Boolean(this.choiceKey(choice))); }
    variantForChoice(size, choice) {
      const key = this.choiceKey(choice);
      if (!key) return null;
      const flavor = this.data.flavors.find(item => item.key === key);
      return this.data.variants.find(variant => variant.size === size && (key === 'variety' ? this.isVariety(variant) : this.normalize(variant.pack) === this.normalize(flavor.variantTitle))) || null;
    }
    mixedVariant(size) { return this.data.variants.find(variant => variant.size === size && this.isMixed(variant)) || null; }
    choiceAvailable(choice) {
      // Inventory belongs to whole-pack variants, not component six-pie stock.
      return Boolean(this.variantForChoice(this.packSize, choice)?.available || this.mixedVariant(this.packSize)?.available);
    }
    resolveVariant() {
      if (!this.complete()) { this.selected = null; return; }
      const keys = new Set(this.boxes.map(choice => this.choiceKey(choice)));
      // Never substitute Mixed boxes for an unavailable homogeneous preset.
      this.selected = keys.size === 1 ? this.variantForChoice(this.packSize, this.boxes[0]) : this.mixedVariant(this.packSize);
    }
    displayVariant(size) {
      if (this.complete()) {
        const same = new Set(this.boxes.map(choice => this.choiceKey(choice))).size === 1;
        const matched = same ? this.variantForChoice(size, this.boxes[0]) : this.mixedVariant(size);
        if (matched) return matched;
      }
      return this.data.variants.find(variant => variant.size === size && this.isVariety(variant))
        || this.data.variants.find(variant => variant.size === size && !this.isMixed(variant))
        || this.mixedVariant(size);
    }
    comparisonPrice(variant, choices = null) {
      if (!variant || !Number.isSafeInteger(variant.price) || variant.price <= 0 || variant.size <= 6) return null;
      const comparisonChoices = choices || Array.from({ length: variant.size / 6 }, () => this.choiceFromVariant(variant) || { mode: 'variety' });
      if (comparisonChoices.length !== variant.size / 6) return null;
      let equivalent = 0;
      for (const choice of comparisonChoices) {
        const six = this.variantForChoice(6, choice);
        if (!six || !Number.isSafeInteger(six.price) || six.price <= 0) return null;
        equivalent += six.price;
      }
      return Number.isSafeInteger(equivalent) && equivalent > variant.price ? equivalent : null;
    }
    renderComparison(element, cents) {
      if (!element) return;
      const shown = Number.isSafeInteger(cents) && cents > 0;
      element.hidden = !shown; element.textContent = shown ? this.money(cents) : '';
      if (shown) {
        const label = this.data.strings.equivalentPrice.replace('[price]', this.money(cents));
        element.setAttribute('aria-label', label); element.setAttribute('title', label);
      } else { element.removeAttribute('aria-label'); element.removeAttribute('title'); }
    }
    chooseSize(size) {
      if (this.busy || ![6, 12, 18, 24].includes(size) || !this.data.variants.some(variant => variant.size === size && variant.available)) return;
      const growing = size > this.packSize;
      this.packSize = size;
      this.boxes = this.boxes.slice(0, size / 6);
      while (this.boxes.length < size / 6) this.boxes.push(null);
      this.box = Math.min(this.box, this.boxes.length);
      if (growing) this.box = this.boxes.findIndex(choice => !this.choiceKey(choice)) + 1;
      this.clearError(); this.render();
    }
    chooseBox(number) {
      if (this.busy || !Number.isInteger(number) || number < 1 || number > this.boxes.length) return;
      this.box = number; this.clearError(); this.render();
    }
    chooseMode(mode) {
      if (this.busy || !['variety', 'single'].includes(mode)) return;
      if (mode === 'variety' && !this.choiceAvailable({ mode })) return;
      if (mode === 'single' && !this.data.flavors.some(flavor => this.choiceAvailable({ mode, flavorKey: flavor.key }))) return;
      this.boxes[this.box - 1] = mode === 'single' && this.mode === 'single' ? this.currentChoice : { mode };
      this.clearError(); this.render();
    }
    chooseFlavor(key) {
      const choice = { mode: 'single', flavorKey: key };
      if (this.busy || !this.data.flavors.some(flavor => flavor.key === key) || !this.choiceAvailable(choice)) return;
      this.boxes[this.box - 1] = choice; this.clearError(); this.render();
    }
    nextUnchosenBox() {
      const indexes = Array.from({ length: this.boxes.length }, (_, offset) => (this.box + offset) % this.boxes.length);
      return indexes.find(index => !this.choiceKey(this.boxes[index])) ?? null;
    }
    boxTitle(choice) {
      if (choice?.mode === 'variety') return this.data.strings.varietyTitle;
      return this.data.flavors.find(flavor => flavor.key === choice?.flavorKey)?.label || this.data.strings.chooseBoxShort;
    }
    flavorCounts() {
      const counts = new Map(this.data.flavors.map(flavor => [flavor.key, 0]));
      for (const choice of this.boxes) {
        if (choice?.mode === 'variety') this.data.flavors.forEach(flavor => counts.set(flavor.key, counts.get(flavor.key) + 1));
        else if (this.choiceKey(choice)) counts.set(choice.flavorKey, counts.get(choice.flavorKey) + 6);
      }
      return this.data.flavors.map(flavor => ({ ...flavor, quantity: counts.get(flavor.key) })).filter(flavor => flavor.quantity > 0);
    }
    packContents() { return this.complete() ? this.flavorCounts().map(flavor => `${flavor.quantity} × ${flavor.label}`).join(', ') : ''; }
    boxContents(choice) {
      return choice.mode === 'variety' ? `${this.data.strings.varietyTitle}: ${this.data.flavors.map(flavor => `1 × ${flavor.label}`).join(', ')}` : `6 × ${this.boxTitle(choice)}`;
    }
    packProperties() {
      if (!this.complete()) return {};
      return Object.fromEntries(this.boxes.map((choice, index) => [`Box ${index + 1}`, this.boxContents(choice)]));
    }
    progress() {
      const chosen = this.boxes.filter(choice => this.choiceKey(choice)).length;
      const remaining = this.boxes.length - chosen;
      if (remaining) {
        if (this.boxes.length === 1) return this.data.strings.chooseBoxFlavors.replace('[count]', 1);
        return (remaining === 1 ? this.data.strings.boxPendingOne : this.data.strings.boxPendingOther).replace('[count]', remaining);
      }
      return this.boxes.length === 1 ? this.data.strings.boxProgressOne
        : this.data.strings.boxProgress.replace('[chosen]', chosen).replace('[boxes]', this.boxes.length);
    }
    selectedTitle() {
      return !this.complete() ? this.progress() : !this.selected?.available ? this.data.strings.unavailableCombination
        : this.boxes.length === 1 ? this.data.strings.packReadyOne : this.data.strings.packReady.replace('[size]', this.packSize).replace('[boxes]', this.boxes.length);
    }
    money(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.data.currency, minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100); }

    render() {
      this.resolveVariant();
      const quantity = this.quantity();
      const displayedVariant = this.selected || this.displayVariant(this.packSize);
      const price = displayedVariant?.price ?? 0;
      const comparison = this.comparisonPrice(displayedVariant, this.complete() ? this.boxes : null);
      const valid = this.complete() && this.selected?.available && quantity > 0 && this.isOpen() && !this.busy;
      this.select.value = this.selected ? String(this.selected.id) : '';
      const properties = this.packProperties();
      this.querySelectorAll('[data-pack-box-property]').forEach(input => {
        const value = properties[`Box ${input.dataset.packBoxProperty}`];
        input.value = value || ''; input.disabled = !value;
      });
      this.flavorFieldset.hidden = false;
      this.querySelector('[data-pack-price]').textContent = this.money(price);
      this.querySelector('[data-pack-total]').textContent = this.money(price * quantity);
      this.querySelector('[data-pack-sticky-total]').textContent = this.money(price * quantity);
      this.renderComparison(this.querySelector('[data-pack-compare]'), comparison);
      this.renderComparison(this.querySelector('[data-pack-total-compare]'), comparison && quantity > 0 ? comparison * quantity : null);
      this.renderComparison(this.querySelector('[data-pack-sticky-compare]'), comparison && quantity > 0 ? comparison * quantity : null);
      const comparisonNote = this.querySelector('[data-pack-comparison-note]'); if (comparisonNote) comparisonNote.hidden = !comparison;
      this.querySelector('[data-pack-count]').textContent = this.data.strings.summary.replace('[packs]', quantity).replace('[pies]', quantity * this.packSize);
      this.querySelector('[data-pack-selection]').textContent = this.selectedTitle();
      this.querySelector('[data-pack-sticky-title]').textContent = this.selectedTitle();
      const progress = this.querySelector('[data-pack-progress]');
      progress.querySelector('[data-pack-progress-label]').textContent = this.complete() ? this.data.strings.editBoxFlavors.replace('[count]', this.box) : this.progress();
      progress.dataset.complete = String(this.complete());
      progress.disabled = this.busy;
      this.querySelector('[data-pack-active-heading]').textContent = this.data.strings.chooseBox.replace('[count]', this.box);
      this.querySelectorAll('[data-pack-size]').forEach(button => {
        const size = Number(button.dataset.packSize), displayed = this.displayVariant(size);
        button.setAttribute('aria-pressed', String(size === this.packSize));
        button.disabled = this.busy || !this.data.variants.some(variant => variant.size === size && variant.available);
        button.querySelector('[data-size-price]').textContent = displayed ? this.money(displayed.price) : this.data.strings.soldOut;
        this.renderComparison(button.querySelector('[data-size-compare]'), this.comparisonPrice(displayed, this.complete() && size === this.packSize ? this.boxes : null));
      });
      this.querySelectorAll('[data-pack-mode]').forEach(button => {
        const mode = button.dataset.packMode;
        button.setAttribute('aria-pressed', String(mode === this.mode));
        button.disabled = this.busy || (mode === 'variety' ? !this.choiceAvailable({ mode }) : !this.data.flavors.some(flavor => this.choiceAvailable({ mode, flavorKey: flavor.key })));
      });
      this.querySelectorAll('[data-pack-flavor]').forEach(button => {
        const key = button.dataset.packFlavor;
        const available = this.choiceAvailable({ mode: 'single', flavorKey: key });
        button.disabled = this.busy || !available;
        button.setAttribute('aria-pressed', String(this.currentChoice?.flavorKey === key));
        button.querySelector('[data-flavor-note]').textContent = available ? this.currentChoice?.flavorKey === key ? this.data.strings.countInPack.replace('[count]', 6) : this.data.strings.flavorPrompt : this.data.strings.soldOut;
      });
      this.querySelectorAll('[data-pack-change]').forEach(button => { button.disabled = this.busy || (Number(button.dataset.packChange) < 0 && quantity <= 1); });
      this.quantityInput.disabled = this.busy;
      const label = this.busy ? this.data.strings.adding : !this.isOpen() ? this.closedLabel()
        : this.complete() && !this.selected?.available ? this.data.strings.soldOut : this.data.strings.add;
      this.querySelectorAll('[data-pack-add], [data-pack-sticky-add]').forEach(button => { button.disabled = !valid; button.textContent = label; });
      const next = this.nextUnchosenBox(), nextButton = this.querySelector('[data-pack-next-box]');
      nextButton.hidden = !this.choiceKey(this.currentChoice) || next === null;
      nextButton.disabled = this.busy;
      if (next !== null) nextButton.textContent = this.data.strings.chooseBoxFlavors.replace('[count]', next + 1);
      this.form.setAttribute('aria-busy', String(this.busy));
      this.renderPreview(); this.renderBoxNavigation(); this.renderContentsList();
    }

    renderPreview() {
      const chosen = !this.choiceKey(this.currentChoice) ? [] : this.mode === 'variety' ? this.data.flavors : Array(6).fill(this.flavor);
      const signature = `${this.box}:${chosen.map(flavor => flavor.key).join(',')}`;
      if (signature === this.previewSignature) return;
      this.previewSignature = signature;
      this.querySelectorAll('[data-pack-slot]').forEach((slot, index) => {
        slot.replaceChildren(); slot.classList.toggle('is-filled', Boolean(chosen[index])); slot.style.setProperty('--slot-index', index);
        const flavor = chosen[index];
        if (!flavor) {
          const number = document.createElement('span'); number.className = 'jalp-pack__empty'; number.textContent = String(index + 1); number.setAttribute('aria-hidden', 'true'); slot.append(number);
          slot.removeAttribute('aria-label'); slot.removeAttribute('title'); delete slot.dataset.flavor; return;
        }
        slot.dataset.flavor = flavor.key;
        if (flavor.boxImage || flavor.image) {
          const image = document.createElement('img'); image.src = flavor.boxImage || flavor.image; image.className = 'jalp-pack__pie-image'; image.alt = flavor.label; image.width = 360; image.height = 360; slot.append(image);
        } else {
          const placeholder = document.createElement('span'); placeholder.className = 'jalp-pack__placeholder'; placeholder.textContent = flavor.label.slice(0, 1); placeholder.setAttribute('aria-hidden', 'true'); slot.append(placeholder);
        }
        slot.setAttribute('aria-label', flavor.label); slot.setAttribute('title', flavor.label);
      });
    }
    renderContentsList() {
      const list = this.querySelector('[data-pack-contents-list]'); list.replaceChildren(); list.hidden = !this.complete();
      if (!this.complete()) return;
      for (const flavor of this.flavorCounts()) { const item = document.createElement('li'); item.textContent = `${flavor.quantity} × ${flavor.label}`; list.append(item); }
    }
    renderBoxNavigation() {
      this.querySelectorAll('[data-pack-box-nav]').forEach(nav => {
        nav.hidden = this.boxes.length < 2;
        if (nav.children.length !== this.boxes.length) {
          nav.replaceChildren();
          this.boxes.forEach((_, index) => {
            const button = document.createElement('button'); button.type = 'button'; button.className = 'jalp-pack__box-tab'; button.dataset.packBox = String(index + 1);
            button.append(document.createElement('strong'), document.createElement('span'));
            button.addEventListener('click', () => this.chooseBox(index + 1)); nav.append(button);
          });
        }
        Array.from(nav.children).forEach((button, index) => {
          button.querySelector('strong').textContent = this.data.strings.boxLabel.replace('[count]', index + 1);
          button.querySelector('span').textContent = this.boxTitle(this.boxes[index]);
          button.setAttribute('aria-pressed', String(index + 1 === this.box));
          button.dataset.complete = String(Boolean(this.choiceKey(this.boxes[index]))); button.disabled = this.busy;
        });
      });
      this.querySelector('[data-pack-caption]').textContent = this.choiceKey(this.currentChoice)
        ? this.data.strings.boxCaption.replace('[count]', this.box).replace('[choice]', this.boxTitle(this.currentChoice))
        : this.data.strings.boxEmpty.replace('[count]', this.box);
    }
    clearError() { this.errorElement.textContent = ''; this.errorElement.hidden = true; }
    showError(message) { this.errorElement.textContent = message; this.errorElement.hidden = false; }
    async addToCart(event) {
      event.preventDefault(); if (this.busy) return; this.clearError();
      if (!this.isOpen()) { this.render(); this.showError(this.closedLabel()); return; }
      if (!this.complete()) { this.showError(this.data.strings.allBoxesRequired); return; }
      this.resolveVariant();
      if (!this.selected?.available) { this.showError(this.data.strings.unavailableCombination); return; }
      const quantity = this.quantity(); if (!quantity) { this.showError(this.data.strings.invalidQuantity); return; }
      const candidate = document.querySelector('cart-drawer');
      const drawer = typeof candidate?.getSectionsToRender === 'function' && typeof candidate?.renderContents === 'function' ? candidate : null;
      const payload = { items: [{ id: this.selected.id, quantity, properties: this.packProperties() }] };
      if (drawer) {
        payload.sections = drawer.getSectionsToRender().map(section => section.section || section.id);
        payload.sections_url = window.location.pathname || this.data.cartUrl;
        drawer.setActiveElement?.(event.submitter || this.querySelector('[data-pack-add]'));
      }
      this.busy = true; this.render();
      try {
        const response = await fetch(this.data.addUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.data.strings.error);
        if (drawer) {
          try {
            if (drawer.renderContents(result) !== false) {
              this.busy = false; this.render(); return;
            }
          } catch { /* The add succeeded; use the full cart if section rendering fails. */ }
        }
        window.location.assign(this.data.cartUrl);
      } catch (error) { this.busy = false; this.render(); this.showError(error instanceof Error ? error.message : this.data.strings.error); }
    }
  }
  customElements.define('jalp-mini-pack', JalpMiniPack);
})();
